import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { useProductDetail } from '@/hooks/useProductDetail';
import { ProductMediaGallery } from '@/components/product/ProductMediaGallery';
import { ProductInfoSidebar } from '@/components/product/ProductInfoSidebar';
import { ProductDetailTabs } from '@/components/product/ProductDetailTabs';
import RelatedProducts from '@/components/product/RelatedProducts';
import { ProductDetailSkeleton } from '@/components/product/ProductDetailSkeleton';
import { ProductNotFound } from '@/components/product/ProductNotFound';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { PurchaseOrderForm } from '@/components/product/PurchaseOrderForm';
import { useAuth } from '@/context/AuthContext';
import { DynamicSeoHead } from '@/components/common/DynamicSeoHead';
import { createProductSchema, createBreadcrumbSchema } from '@/utils/schemaUtils';
import { generateProductSeoTitle, generateProductSeoDescription, createProductBreadcrumbs } from '@/utils/seoUtils';
import ErrorBoundary from '@/components/ErrorBoundary';
import { extractProductId, createProductUrl, validateProductUrl } from '@/utils/slugUtils';

const ProductDetail = () => {
  // All hooks are now at the top
  const { idSlug } = useParams<{ idSlug: string }>();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [isPurchaseFormOpen, setIsPurchaseFormOpen] = useState(false);
  const toastShownRef = useRef(false);
  
  // Extract the actual ID from the URL parameter (could be "123" or "123-product-name")
  const productId = idSlug ? extractProductId(idSlug) : undefined;
  
  const { 
    product, 
    isLoading, 
    isFavorited,
    handleFavoriteToggle
  } = useProductDetail(productId);

  const showContact = searchParams.get('contact') === 'true';
  const showBuyForm = searchParams.get('buy') === 'true';
  
  const isOwnProduct = product && user && product.user_id === user.id;

  // Check if current URL format is correct and redirect if needed
  useEffect(() => {
    if (product && idSlug && productId) {
      const currentPath = window.location.pathname;
      const correctUrl = createProductUrl(productId, product.title);
      
      // Only redirect if the URL doesn't match and we're not already on the correct format
      if (!validateProductUrl(productId, product.title, currentPath)) {
        // Preserve query parameters when redirecting
        const search = window.location.search;
        navigate(correctUrl + search, { replace: true });
        return;
      }
    }
  }, [product, idSlug, productId, navigate]);

  // SEO data calculation
  const productUrl = `https://treguim.org${product ? createProductUrl(productId || '', product.title) : `/product/${productId}`}`;
  
  const seoTitle = useMemo(() => {
    if (!product) return 'Produkti | TreguIm';
    return generateProductSeoTitle(
      product.title,
      product.location,
      product.category?.name
    );
  }, [product]);

  const seoDescription = useMemo(() => {
    if (!product) return 'Shiko detaje të produktit në TreguIm';
    return generateProductSeoDescription(
      product.title,
      product.price,
      product.location,
      product.condition,
      product.category?.name
    );
  }, [product]);

  const productSchema = useMemo(() => product ? createProductSchema(product, productUrl) : null, [product, productUrl]);
  
  const breadcrumbSchema = useMemo(() => {
    if (!product || !productId) return null;
    const breadcrumbs = createProductBreadcrumbs(
      product.title,
      productId,
      product.category?.name,
      undefined // subcategory not available in current data structure
    );
    return createBreadcrumbSchema(breadcrumbs);
  }, [product, productId]);

  const productImage = useMemo(() => {
    if (product?.images?.length > 0 && product.images[0]) {
      return `https://treguim.org/sharing-image?url=${encodeURIComponent(product.images[0])}`;
    }
    return "https://treguim.org/sharing-default.jpg";
  }, [product]);

  useEffect(() => {
    if (showBuyForm && product) {
      if (isOwnProduct) {
        toast({
          title: "Blerje e refuzuar",
          description: "Nuk mund të blini produktin tuaj. Ju lutem kontrolloni produktet e tjera.",
          variant: "destructive"
        });
        return;
      }
      
      if (product.delivery_option !== 'pickup_only') {
        setIsPurchaseFormOpen(true);
      }
    }
  }, [showBuyForm, product, isOwnProduct, toast]);
  
  useEffect(() => {
    if (showBuyForm && product && !toastShownRef.current) {
      toastShownRef.current = true;
      
      if (isOwnProduct) {
        return;
      }
      
      if (product.delivery_option === 'pickup_only') {
        toast({
          title: "Dërgesë e padisponueshme",
          description: "Ky produkt është vetëm për tërheqje. Ju lutemi kontaktoni shitësin.",
          variant: "destructive"
        });
      } else {
        const toastAction = {
          title: "Formulari i blerjes",
          description: "Ju lutemi plotësoni detajet e pagesës dhe dërgesës për të përfunduar blerjen.",
          action: (
            <Button 
              onClick={() => setIsPurchaseFormOpen(true)}
              className="bg-brand-500 text-white px-3 py-1 rounded-md text-xs"
            >
              Plotëso Formularin
            </Button>
          )
        };
        
        setTimeout(() => {
          toast(toastAction);
        }, 0);
      }
    }
  }, [showBuyForm, product, isOwnProduct, toast]); 
  
  // Early returns are now after all hook calls
  if (isLoading) {
    return <ProductDetailSkeleton />;
  }
  
  if (!product) {
    return <ProductNotFound />;
  }
  
  const handleBuyNow = () => {
    if (isOwnProduct) {
      toast({
        title: "Blerje e refuzuar",
        description: "Nuk mund të blini produktin tuaj. Ju lutem kontrolloni produktet e tjera.",
        variant: "destructive"
      });
      return;
    }

    if (product.delivery_option === 'pickup_only') {
      toast({
        title: "Dërgesë e padisponueshme",
        description: "Ky produkt është vetëm për tërheqje. Ju lutemi kontaktoni shitësin.",
        variant: "destructive"
      });
      return;
    }
    
    if (!user) {
      toast({
        title: "Kërkohet autentifikimi",
        description: "Ju lutem kyçuni për të blerë këtë produkt",
      });
      const correctUrl = createProductUrl(productId || '', product.title);
      navigate('/auth?redirect=' + encodeURIComponent(`${correctUrl}?buy=true`));
      return;
    }
    
    setIsPurchaseFormOpen(true);
  };
  
  const imageWidth = 1200;
  const imageHeight = 630;
  
  return (
    <ErrorBoundary>
      <div className="flex flex-col min-h-screen">
        <DynamicSeoHead
          title={seoTitle}
          description={seoDescription}
          ogType="product"
          ogImage={productImage}
          url={productUrl}
          structuredData={[productSchema, breadcrumbSchema].filter(Boolean)}
          entityName={product.title}
          category={product.category?.name}
          location={product.location}
          price={product.price}
          condition={product.condition}
        />
        
        <Helmet>
          <meta property="og:price:amount" content={String(product?.price)} />
          <meta property="og:price:currency" content={product?.currency || 'EUR'} />
          <meta property="product:price:amount" content={String(product?.price)} />
          <meta property="product:price:currency" content={product?.currency || 'EUR'} />
          <meta property="og:availability" content={product?.stock && product.stock > 0 ? "instock" : "oos"} />
          
          <meta property="og:image:width" content={String(imageWidth)} />
          <meta property="og:image:height" content={String(imageHeight)} />
          <meta property="og:image:alt" content={product?.title || 'Product image'} />
          <meta property="og:image:type" content="image/jpeg" />
          
          <meta name="author" content={product?.seller?.full_name || 'TreguIm'} />
          <meta name="geo.placename" content={product?.location} />
        </Helmet>
        
        <Navbar />
        
        <main className="flex-grow pt-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-6">
              <nav className="text-sm text-gray-500">
                <ol className="list-none p-0 inline-flex">
                  <li className="flex items-center">
                    <Link to="/" className="hover:text-brand-500">Kryefaqja</Link>
                    <span className="mx-2">/</span>
                  </li>
                  {product?.category && (
                    <li className="flex items-center">
                      <Link to={`/search?category=${product.category.id}`} className="hover:text-brand-500">
                        {product.category.name}
                      </Link>
                      <span className="mx-2">/</span>
                    </li>
                  )}
                  <li className="text-gray-700">{product?.title}</li>
                </ol>
              </nav>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <ProductMediaGallery 
                  images={product.images} 
                  videoUrl={product.video_url}
                  title={product.title} 
                />
              </div>
              
              <div>
                <ProductInfoSidebar 
                  product={product}
                  isFavorited={isFavorited}
                  onFavoriteToggle={handleFavoriteToggle}
                  onBuyNow={handleBuyNow}
                />
              </div>
            </div>
            
            <div className="mt-8">
              <ProductDetailTabs 
                product={product} 
                defaultTab={showBuyForm || showContact ? "contact" : "description"}
              />
            </div>
            
            {product.category?.id && (
              <div className="mt-12">
                <h2 className="text-2xl font-semibold mb-6">Produkte të ngjashme</h2>
                <RelatedProducts 
                  categoryId={product.category.id}
                  currentProductId={productId || ''}
                />
              </div>
            )}
          </div>
        </main>
        
        {product && (
          <PurchaseOrderForm
            open={isPurchaseFormOpen}
            onOpenChange={setIsPurchaseFormOpen}
            productId={product.id}
            productTitle={product.title}
            productImage={product.images?.[0]}
            price={product.price}
            currency={product.currency}
            sellerId={product.user_id}
          />
        )}
        
        <Footer />
      </div>
    </ErrorBoundary>
  );
};

export default ProductDetail;
